<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ReadMoreIndowStories from '$lib/PageSections/ReadMoreIndowStories/ReadMoreIndowStories.svelte';

	import closeupIndowWindowInsertForNoiseReductionImg from './images/closeupIndowWindowInsertForNoiseReduction.jpg';
	import familyOutsideHouseImg from './images/familyOutsideHouse.jpg';
	import maggieImg from './images/maggie.jpg';
	import maggieTestimonialImg from './images/maggieTestimonial.png';
	import noisyTrafficJamImg from './images/noisy-traffic-jam.jpeg';
	import stcRatingImg from './images/stc-rating.jpg';
	import streetNoiseReductionThroughPlantsImg from './images/street-noise-reduction-through-plants.jpg';
</script>

<Section>
	<h2>How to Achieve Street Noise Reduction</h2>
	<p>
		Maggie was routinely being woken up by traffic noise outside her 1913 home.
		Her space wasn’t built for current noise. Even modern homes can’t provide
		traffic noise reduction at the rate home delivery trucks increase. Maggie
		and her family found peace with Indow inserts, but here are a few tips that
		can help any home:
	</p>
	<Image
		src={noisyTrafficJamImg}
		alt="noisy traffic jam needing street noise reduction"
	/>
	<p>
		<strong>1. Assess your space.</strong> Traffic noise reduction starts with identifying
		where the noise is coming into your space. Any transitional space from wall to
		door or window is an invitation for cracks or breaks in seals. This is the most
		likely place where air and sound will be entering your home.
	</p>
	<p>
		Feel with your hands (or hold a lit match to see if it gets blown out) near
		these spaces to find the most offending areas of your home.
	</p>
	<p>
		<strong>2. Know which products help reduce noise.</strong>
		<a href="/resources/blog/soundproof-windows-home-depot"
			>Many products claim to reduce noise</a
		>. Not all of them reduce the kind of noise that is affecting your home or
		are appropriate for your type of home.
		<a href="/resources/stc-rating">STC ratings</a> compare noise reducing products
		apples to apples.
	</p>
	<Image
		src={stcRatingImg}
		alt="stc ratings show which products help reduce noise"
	/>
	<p>
		STC stands for Sound Transmission Class and this rating will tell you
		exactly how much noise a product will absorb and/or reflect. Look for it
		online or on product labels as you shop.
	</p>
	<p>
		<strong>3. Reduce Traffic noise with plants.</strong> The denser and fuller
		the foliage, the more it will block and absorb noise. Much like your home,
		if there is a crack or weak spot, the noise will leak through. A good tree
		buffer that includes a landform can reduce street noise by 50% (10 – 15 dBA)
		according to
		<a
			href="https://www.fs.usda.gov/nac/buffers/guidelines/6_aesthetics/4.html"
			target="_blank"
			rel="external">USDA National Agroforestry Center</a
		>
	</p>
	<Image
		src={streetNoiseReductionThroughPlantsImg}
		alt="chart showing how to reduce street noise with plants and landforms"
	/>
	<h3>
		<a href="/resources/soundproofing-windows"
			>Read About Soundproofing Your Windows</a
		>
	</h3>
</Section>

<Section>
	<h2>How Maggie Found Relief from Daily Traffic Noise</h2>
	<p>
		Even though Maggie and her partner Don had just bought their home, they were
		almost ready to sell it. Until they moved in, they didn’t realize just how
		much traffic noise would filter through their windows from the nearby city
		traffic and auto body shop. After three weeks, they knew the house needed
		significant street noise reduction.
	</p>
	<Image
		src={maggieImg}
		alt="woman and child in home with indow inserts for street noise reduction"
	/>
</Section>

<Section>
	<h2>Reducing Traffic Noise for Better Sleep</h2>
	<p>
		Maggie had to sleep with earplugs and still got woken up nightly. The
		traffic noise was especially hard since their infant son Max needed his
		naps. Lack of sleep can have lasting effects, and so can noise pollution.
		Continuous street noise can lead to stress, high blood pressure, even
		adverse effects on children’s developmental progress.
	</p>
	<p>
		The windows of the 1913 Craftsman also leaked air badly, scoring the lowest
		possible on an air leakage test. But replacing them for street noise
		reduction wasn’t an option. Like many owners of historic homes, the original
		wavy leaded glass was one reason Maggie and Don fell for the house.
	</p>
	<Image
		src={closeupIndowWindowInsertForNoiseReductionImg}
		alt="closeup of Indow window insert used for street noise reduction"
	/>
</Section>

<Section>
	<h2>Street Noise Reduction Strategy</h2>
	<p>
		What could this new family do to reduce street noise and create a quiet
		home? Would quiet windows solve their problems? They tried installing Indow
		window inserts in their first floor bedrooms, dining room, and living room.
	</p>
	<p>
		Their quality of life improved dramatically after reducing traffic noise.
		The outside sound is so muffled, no one wakes up in the middle of the night.
		They also achieved energy efficiency without replacing the gorgeous windows
		they fell for. They no longer talk about moving.
	</p>
	<p>
		Indow inserts are perfect for reducing street noise because they perform
		sound reduction. Once installed into a window, they create an air seal to
		block sound vibrations carried by air. The acrylic they are made of is also
		better at mitigating sound than glass.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonial</h2>
	<Image
		src={maggieTestimonialImg}
		alt="family next to window with Indow inserts used for traffic noise reduction"
	/>
	<p class="text-xl text-indow-blue! italic">
		“Indow Windows have made a huge difference and the inserts are nearly
		invisible. They’re not like storm windows, they take just a second to pop
		out and pop back in and you don’t have to go outside.”
	</p>
	<p>– Maggie Finnerty, Home Owner, Portland, Oregon.</p>
	<p class="italic">
		<a href="/resources/case-studies">Read more case studies</a>
	</p>
</Section>

<Section>
	<Image
		src={familyOutsideHouseImg}
		alt="family outside house with Indow window inserts used for street noise reduction"
	/>
	<h2>Easy to Remove & Reinstall</h2>
	<p>
		Some refer to Indow inserts as interior storm windows, but Maggie doesn’t
		consider them that. Her old house had storm windows she never once took off.
		In her new house, when they burn something cooking, they easily remove the
		Indow inserts and open all the windows to catch a fresh breeze. And then
		promptly put them back in to stop traffic noise.
	</p>
	<p>
		Read more about street noise reduction and home soundproofing: <a
			href="/resources/home-soundproofing">Home Soundproofing Tips</a
		>.
	</p>
</Section>
<hr />
<ReadMoreIndowStories />
<GetAFreeEstimate />
